import React, { createContext, useContext, useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { cn } from '../lib/utils';

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check local storage or system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeToggle = ({ className }) => {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={cn(
        "relative p-2 rounded-full transition-all duration-500 ease-in-out",
        "bg-gray-100 dark:bg-gray-800",
        "hover:bg-gray-200 dark:hover:bg-gray-700",
        "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500",
        "border border-gray-200 dark:border-gray-700",
        "shadow-sm hover:shadow-md",
        "group overflow-hidden",
        className
      )}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      <div className="relative w-5 h-5">
        {/* Sun Icon */}
        <Sun
          className={cn(
            "absolute inset-0 w-5 h-5 transition-all duration-700 ease-in-out",
            "text-amber-500 dark:text-amber-400",
            theme === 'light' 
              ? 'rotate-0 scale-100 opacity-100 transform' 
              : 'rotate-180 scale-75 opacity-0 transform'
          )}
          style={{
            filter: theme === 'light' ? 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.4))' : 'none'
          }}
        />
        
        {/* Moon Icon */}
        <Moon
          className={cn(
            "absolute inset-0 w-5 h-5 transition-all duration-700 ease-in-out",
            "text-slate-600 dark:text-slate-300",
            theme === 'dark' 
              ? 'rotate-0 scale-100 opacity-100 transform' 
              : '-rotate-180 scale-75 opacity-0 transform'
          )}
          style={{
            filter: theme === 'dark' ? 'drop-shadow(0 0 8px rgba(100, 116, 139, 0.4))' : 'none'
          }}
        />
      </div>
      
      {/* Subtle background animation */}
      <div 
        className={cn(
          "absolute inset-0 rounded-full transition-all duration-700 ease-in-out opacity-20",
          theme === 'light' 
            ? 'bg-gradient-to-r from-amber-200 to-orange-200 scale-100' 
            : 'bg-gradient-to-r from-slate-600 to-indigo-600 scale-0'
        )}
      />
    </button>
  );
}; 